import { useState, useMemo } from "react";
import { Link } from "wouter";
import { SmartLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { useListPublicGyms } from "@workspace/api-client-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Search, MapPin, Users, Building2, ArrowRight } from "lucide-react";

export default function ExploreGyms() {
  const [search, setSearch] = useState("");
  const { data: gyms, isLoading } = useListPublicGyms();

  const filtered = useMemo(() => {
    if (!gyms) return [];
    if (!search.trim()) return gyms;
    const q = search.toLowerCase();
    return gyms.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.address && g.address.toLowerCase().includes(q))
    );
  }, [gyms, search]);

  return (
    <SmartLayout>
      <SEOHead
        title="Explore Martial Arts Gyms — Find Academies Near You"
        description="Browse martial arts gyms and academies on Open Mat. Find the right place to train, view trainers, locations, and book private lessons."
        canonicalPath="/explore/gyms"
      />

      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
              Explore Gyms
            </h1>
            <p className="text-white/50 text-lg max-w-xl mx-auto">
              Browse martial arts gyms and academies. Find the right place to train.
            </p>
          </motion.div>

          <div className="max-w-md mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <Input
                placeholder="Search by name or location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 rounded-xl focus:border-primary/50"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-64 bg-white/[0.03] rounded-2xl animate-pulse border border-white/[0.06]" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50 text-lg">No gyms found matching your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((gym, i) => (
                <motion.div
                  key={gym.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link href={`/gym/${gym.slug}`}>
                    <div className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 hover:bg-white/[0.04] transition-all duration-300 hover:-translate-y-1 cursor-pointer h-full flex flex-col">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                          {gym.logoUrl ? (
                            <img src={gym.logoUrl} alt={`${gym.name} logo`} className="w-full h-full object-cover rounded-xl" loading="lazy" />
                          ) : (
                            <Building2 className="w-6 h-6 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h2 className="text-xl font-display font-bold text-white group-hover:text-primary transition-colors truncate">
                            {gym.name}
                          </h2>
                          {gym.address && (
                            <p className="text-sm text-white/40 flex items-center gap-1 mt-1">
                              <MapPin className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">{gym.address}</span>
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mt-auto pt-4 border-t border-white/[0.06]">
                        <span className="text-xs text-white/40 flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {gym.trainerCount} trainer{gym.trainerCount !== 1 ? "s" : ""}
                        </span>
                        <span className="text-xs text-white/40 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {gym.locationCount} location{gym.locationCount !== 1 ? "s" : ""}
                        </span>
                        <ArrowRight className="w-4 h-4 text-primary ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </SmartLayout>
  );
}
